import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Paper, Typography, Button, Chip, Alert, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton,
    Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, Collapse
} from '@mui/material';
import {
    Upload as UploadIcon, Delete as DeleteIcon,
    Add as AddIcon,
    Compare as CompareIcon,
    Close as CloseIcon, Download as DownloadIcon,
    Visibility as VisibilityIcon,
    HelpOutline as HelpOutlineIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const RawDataRow = React.memo(({ q, idx, canEdit, isAdmin, onEdit, onSoftDelete, onHardDelete }) => (
    <TableRow hover sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#f9f9f9' }}>
        <TableCell sx={{ fontSize: '11px', fontWeight: 600 }}>{q.vendorName}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.vendorMobile}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.contactPerson}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.city || q.location}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.state}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.material}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.make}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.unit}</TableCell>
        <TableCell sx={{ fontSize: '11px', textAlign: 'right' }}>{q.qty}</TableCell>
        <TableCell sx={{ fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>{q.rate}</TableCell>
        <TableCell sx={{ fontSize: '11px' }}>
            <Chip label={q.billType} size="small" color={q.billType === 'GST' ? 'success' : 'default'} />
        </TableCell>
        <TableCell sx={{ fontSize: '11px' }}>{q.remarks}</TableCell>
        {canEdit && (
            <TableCell>
                <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => onEdit(q)}>
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Move to Trash">
                    <IconButton size="small" color="warning" onClick={() => onSoftDelete(q.id)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {isAdmin && (
                    <Tooltip title="Permanent Delete">
                        <IconButton size="small" color="error" onClick={() => onHardDelete(q.id)}>
                            <DeleteIcon fontSize="small" sx={{ opacity: 0.6 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        )}
    </TableRow>
));

const VendorComparisonPage = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const isManager = user?.role === 'manager';
    const isVendorManager = user?.role === 'vendor_manager';
    const canEdit = isAdmin || isManager || isVendorManager;

    const [comparisons, setComparisons] = useState([]);
    const [selectedComparison, setSelectedComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState(0); // 0=pivot, 1=raw
    const [helpExpanded, setHelpExpanded] = useState(false);
    const [editQuoteOpen, setEditQuoteOpen] = useState(false);
    const [editQuote, setEditQuote] = useState(null);
    const [showTrash, setShowTrash] = useState(false);
    const [trashedQuotes, setTrashedQuotes] = useState([]);

    // Upload state
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadNotes, setUploadNotes] = useState('');
    const [uploading, setUploading] = useState(false);

    // Manual entry state
    const [manualOpen, setManualOpen] = useState(false);
    const [manualRows, setManualRows] = useState([{
        vendorName: '', vendorMobile: '', contactPerson: '', city: '', state: '', material: '',
        make: '', unit: '', qty: '', rate: '', billType: 'GST', remarks: ''
    }]);

    // Filters
    const [filterVendor, setFilterVendor] = useState('');
    const [filterMaterial, setFilterMaterial] = useState('');
    const [filterMake, setFilterMake] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [filterState, setFilterState] = useState('');
    const [filterBillType, setFilterBillType] = useState('');

        const [debouncedFilterVendor, setDebouncedFilterVendor] = useState('');
    const [debouncedFilterMaterial, setDebouncedFilterMaterial] = useState('');
    const [debouncedFilterMake, setDebouncedFilterMake] = useState('');
    const [debouncedFilterCity, setDebouncedFilterCity] = useState('');
    const [debouncedFilterState, setDebouncedFilterState] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilterVendor(filterVendor);
            setDebouncedFilterMaterial(filterMaterial);
            setDebouncedFilterMake(filterMake);
            setDebouncedFilterCity(filterCity);
            setDebouncedFilterState(filterState);
        }, 300);
        return () => clearTimeout(timer);
    }, [filterVendor, filterMaterial, filterMake, filterCity, filterState]);

    const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

    const fetchComparisons = useCallback(async () => {
        setLoading(true);
        setSelectedComparison(null);
        try {
            const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/global`, authHeaders());
            const list = res.data.data.comparisons;
            setComparisons(list);
            if (list.length > 0) fetchComparisonDetail(list[0].id);
        } catch (e) { setError('Failed to load comparisons'); }
        finally { setLoading(false); }
    }, []);

    const fetchComparisonDetail = async (id) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/${id}`, authHeaders());
            setSelectedComparison(res.data.data.comparison);
        } catch (e) { setError('Failed to load comparison details'); }
    };

    useEffect(() => { fetchComparisons(); }, [fetchComparisons]);

    const handleUpload = async () => {
        if (!uploadFile) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('comparisonFile', uploadFile);
            if (uploadNotes) formData.append('notes', uploadNotes);
            const res = await axios.post(`${API_BASE_URL}/vendor-comparisons/upload`, formData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
            });
            setSuccess(`${res.data.data.comparison.versionLabel} uploaded!`);
            setUploadOpen(false); setUploadFile(null); setUploadNotes('');
            fetchComparisons();
        } catch (e) { setError(e.response?.data?.message || 'Upload failed'); }
        finally { setUploading(false); }
    };

    const handleDownloadTemplate = () => {
        const XLSX = require('xlsx');
        const templateData = [
            {
                'Vender Name': 'Aman',
                'Vender Mobile No.': '9958599023',
                'Contact Person': 'Aman Kumar',
                'City': 'Dwarka',
                'State': 'Delhi',
                'Material': 'TMT Bar 16MM',
                'Make': 'JSW',
                'Unit': 'KG',
                'Qty': 1,
                'Rate': 58,
                'GST Bill /Non GST Bill': 'GST Bill',
                'Remarks': ''
            },
            {
                'Vender Name': 'Sakshi',
                'Vender Mobile No.': '9625166309',
                'Contact Person': 'Sakshi Sharma',
                'City': 'Ghaziabad',
                'State': 'Uttar Pradesh',
                'Material': 'TMT Bar 16MM',
                'Make': 'Kamdhenu',
                'Unit': 'KG',
                'Qty': 1,
                'Rate': 63,
                'GST Bill /Non GST Bill': 'Non GST Bill',
                'Remarks': ''
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vendor Comparison');
        XLSX.writeFile(wb, 'vendor_comparison_template.xlsx');
    };

    const handleAddManual = async () => {
        const validRows = manualRows.filter(r => r.vendorName && r.material && r.vendorMobile && r.contactPerson && r.city && r.state);
        if (validRows.length === 0) { setError('At least one complete row is required (Vendor Name, Mobile, Contact Person, City, State, and Material are all mandatory)'); return; }
        try {
            if (!selectedComparison) {
                const res = await axios.post(`${API_BASE_URL}/vendor-comparisons/init`, {}, authHeaders());
                await axios.post(`${API_BASE_URL}/vendor-comparisons/${res.data.data.comparison.id}/quotes`,
                    { quotes: validRows }, authHeaders());
                setSuccess('Quotes added!');
                fetchComparisons();
            } else {
                await axios.post(`${API_BASE_URL}/vendor-comparisons/${selectedComparison.id}/quotes`,
                    { quotes: validRows }, authHeaders());
                setSuccess('Quotes added!');
                fetchComparisonDetail(selectedComparison.id);
            }
            setManualOpen(false);
            setManualRows([{ vendorName: '', vendorMobile: '', contactPerson: '', city: '', state: '', material: '', make: '', unit: '', qty: '', rate: '', billType: 'GST', remarks: '' }]);
        } catch (e) { setError('Failed to add quotes'); }
    };

    const fetchTrashedQuotes = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/trash`, authHeaders());
            setTrashedQuotes(res.data.data.quotes || []);
        } catch (e) { setError('Failed to fetch trash'); }
    };

    const handleSoftDelete = async (quoteId) => {
        if (!window.confirm('Move this quote to trash?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/vendor-comparisons/quotes/${quoteId}/soft`, authHeaders());
            setSuccess('Quote moved to trash');
            fetchComparisonDetail(selectedComparison.id);
            if (showTrash) fetchTrashedQuotes();
        } catch (e) { setError('Failed to delete quote'); }
    };

    const handleHardDelete = async (quoteId) => {
        if (!window.confirm('Permanently delete this quote? Cannot be recovered.')) return;
        try {
            await axios.delete(`${API_BASE_URL}/vendor-comparisons/quotes/${quoteId}/hard`, authHeaders());
            setSuccess('Quote permanently deleted');
            fetchTrashedQuotes();
        } catch (e) { setError('Failed to delete quote'); }
    };

    const handleRestoreQuote = async (quoteId) => {
        try {
            await axios.put(`${API_BASE_URL}/vendor-comparisons/quotes/${quoteId}/restore`, {}, authHeaders());
            setSuccess('Quote restored');
            fetchTrashedQuotes();
            fetchComparisonDetail(selectedComparison.id);
        } catch (e) { setError('Failed to restore quote'); }
    };

    const handleUpdateQuote = async () => {
        try {
            await axios.put(`${API_BASE_URL}/vendor-comparisons/quotes/${editQuote.id}`, editQuote, authHeaders());
            setSuccess('Quote updated');
            setEditQuoteOpen(false);
            setSelectedComparison(prev => ({
                ...prev,
                quotes: prev.quotes.map(q => q.id === editQuote.id ? { ...q, ...editQuote } : q)
            }));
        } catch (e) { setError('Failed to update quote'); }
    };

    const filteredQuotes = useMemo(() => {
        if (!selectedComparison?.quotes) return [];
        let quotes = selectedComparison.quotes;
         if (debouncedFilterVendor) quotes = quotes.filter(q => q.vendorName.toLowerCase().includes(debouncedFilterVendor.toLowerCase()));
       if (debouncedFilterMaterial) quotes = quotes.filter(q => q.material.toLowerCase().includes(debouncedFilterMaterial.toLowerCase()));
       if (debouncedFilterMake) quotes = quotes.filter(q => (q.make || '').toLowerCase().includes(debouncedFilterMake.toLowerCase()));
       if (debouncedFilterCity) quotes = quotes.filter(q => (q.city || q.location || '').toLowerCase().includes(debouncedFilterCity.toLowerCase()));
       if (debouncedFilterState) quotes = quotes.filter(q => (q.state || '').toLowerCase().includes(debouncedFilterState.toLowerCase()));
        if (filterBillType) quotes = quotes.filter(q => q.billType === filterBillType);
        return quotes;
    }, [selectedComparison, debouncedFilterVendor, debouncedFilterMaterial, debouncedFilterMake, debouncedFilterCity, debouncedFilterState, filterBillType]);

    const { materials, vendors, pivotMap } = useMemo(() => {
        const quotes = filteredQuotes;
        if (quotes.length === 0) return { materials: [], vendors: [], pivotMap: {} };
        const materials = [...new Set(quotes.map(q => q.material))].sort();
        const vendors = [...new Set(quotes.map(q => q.vendorName))].sort();
        const pivotMap = {};
        quotes.forEach(q => {
            if (!pivotMap[q.material]) pivotMap[q.material] = {};
            if (!pivotMap[q.material][q.vendorName]) pivotMap[q.material][q.vendorName] = [];
            pivotMap[q.material][q.vendorName].push(q);
        });
        return { materials, vendors, pivotMap };
    }, [filteredQuotes]);

    const vendorColors = ['#e8f5e9', '#e3f2fd', '#fff3e0', '#fce4ec', '#f3e5f5', '#e0f7fa', '#f9fbe7'];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Vendor Comparison</Typography>
                {canEdit && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setManualOpen(true)}>Add Manual</Button>
                        <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>Upload Excel</Button>
                        {isAdmin && (
                            <Button variant="outlined" color={showTrash ? 'error' : 'inherit'}
                                onClick={() => { setShowTrash(!showTrash); if (!showTrash) fetchTrashedQuotes(); }}>
                                {showTrash ? 'Hide Trash' : 'Trash'}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            <Paper variant="outlined" sx={{ mb: 2, borderColor: '#c5cae9', overflow: 'hidden' }}>
                <Box
                    onClick={() => setHelpExpanded(!helpExpanded)}
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1, cursor: 'pointer', bgcolor: '#e8eaf6',
                        '&:hover': { bgcolor: '#dde1f5' }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HelpOutlineIcon fontSize="small" sx={{ color: '#3949ab' }} />
                        <Typography variant="body2" fontWeight={600} color="#3949ab">
                            How this page works
                        </Typography>
                    </Box>
                    {helpExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
                <Collapse in={helpExpanded}>
                    <Box sx={{ px: 2, py: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                📊 Pivot View tab
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.8 }}>
                                Shows a price comparison grid — materials down the left, vendors (and their makes) across the top.
                                Each cell is the <strong>rate</strong> a vendor quoted for that material.<br />
                                • <strong>Grand Total</strong> row/column sums <strong>rates only</strong> — it's for quick price comparison, not a purchase-cost total (that would need Qty × Rate).<br />
                                • <strong>Qty / Unit</strong> columns show what was quoted. If all vendors quoted the same amount, you'll see that number. If vendors quoted <strong>different</strong> quantities or units for the same material, the cell shows <strong>"Varies"</strong> — hover over it to see each vendor's exact quantity and unit.
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                📋 Raw Data tab
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.8 }}>
                                Shows every individual quote as its own row, with full vendor details — name, mobile, contact person,
                                city, state, exact quantity, unit, rate, and bill type.<br />
                                Use this tab when you need the precise numbers behind a "Varies" cell in Pivot View, or to edit/delete a specific quote.
                            </Typography>
                        </Box>
                    </Box>
                </Collapse>
            </Paper>

            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

            {!loading && (
                <>
                    {!selectedComparison ? (
                        <Paper sx={{ p: 6, textAlign: 'center' }}>
                            <CompareIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">No vendor quotes yet — use Add Manual or Upload Excel to get started</Typography>
                        </Paper>
                    ) : (
                        <Paper sx={{ p: 0 }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                                    <Tab label="Pivot View" />
                                    <Tab label="Raw Data" />
                                </Tabs>
                            </Box>

                            <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <TextField size="small" label="Vendor" value={filterVendor} onChange={e => setFilterVendor(e.target.value)} sx={{ width: 140 }} />
                                <TextField size="small" label="Material" value={filterMaterial} onChange={e => setFilterMaterial(e.target.value)} sx={{ width: 140 }} />
                                <TextField size="small" label="Make" value={filterMake} onChange={e => setFilterMake(e.target.value)} sx={{ width: 120 }} />
                                <TextField size="small" label="City" value={filterCity} onChange={e => setFilterCity(e.target.value)} sx={{ width: 120 }} />
                                <TextField size="small" label="State" value={filterState} onChange={e => setFilterState(e.target.value)} sx={{ width: 120 }} />
                                <TextField size="small" select label="Bill Type" value={filterBillType} onChange={e => setFilterBillType(e.target.value)} sx={{ width: 130 }}>
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="GST Bill">GST Bill</MenuItem>
                                    <MenuItem value="Non GST Bill">Non GST Bill</MenuItem>
                                </TextField>
                                {(filterVendor || filterMaterial || filterMake || filterCity || filterState || filterBillType) && (
                                    <Button size="small" onClick={() => { setFilterVendor(''); setFilterMaterial(''); setFilterMake(''); setFilterCity(''); setFilterState(''); setFilterBillType(''); }}>
                                        Clear Filters
                                    </Button>
                                )}
                            </Box>

                            {activeTab === 0 && (
                                <Box sx={{ overflow: 'auto', maxHeight: '75vh' }}>
                                    {materials.length === 0 ? (
                                        <Box sx={{ p: 4, textAlign: 'center' }}>
                                            <Typography color="text.secondary">No data matches the filters</Typography>
                                        </Box>
                                    ) : (
                                        <table style={{ borderCollapse: 'collapse', minWidth: 'max-content', width: '100%', fontSize: '13px', fontFamily: 'Calibri, Arial, sans-serif' }}>                                            <thead>
                                            <tr>
                                                <th style={{ ...thStyle, width: 180, maxWidth: 180, position: 'sticky', left: 0, zIndex: 3, background: '#1a237e', color: 'white' }}>Material</th>
                                                <th style={{ ...thStyle, minWidth: 85, position: 'sticky', left: 180, zIndex: 3, background: '#1a237e', color: 'white' }}>Unit</th>
                                                <th style={{ ...thStyle, minWidth: 85, position: 'sticky', left: 260, zIndex: 3, background: '#1a237e', color: 'white' }}>Qty</th>
                                                {vendors.map((vendor, vIdx) => {
                                                    const makes = [...new Set(
                                                        (selectedComparison?.quotes || [])
                                                            .filter(q => q.vendorName === vendor)
                                                            .map(q => q.make || 'NA')
                                                    )];
                                                    return (
                                                        <th key={vendor} colSpan={makes.length}
                                                            style={{ ...thStyle, background: vendorColors[vIdx % vendorColors.length], color: '#1a237e', borderBottom: '2px solid #1a237e', minWidth: 130 }}>
                                                            {vendor}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                            <tr>
                                                <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 3, background: '#1a237e', color: 'white', fontSize: '11px' }}>—</th>
                                                <th style={{ ...thStyle, position: 'sticky', left: 180, zIndex: 3, background: '#1a237e', color: 'white', fontSize: '11px' }}>—</th>
                                                <th style={{ ...thStyle, position: 'sticky', left: 260, zIndex: 3, background: '#1a237e', color: 'white', fontSize: '11px' }}>—</th>
                                                {vendors.map((vendor, vIdx) => {
                                                    const makes = [...new Set(
                                                        (selectedComparison?.quotes || [])
                                                            .filter(q => q.vendorName === vendor)
                                                            .map(q => q.make || 'NA')
                                                    )];
                                                    const vendorQuote = (selectedComparison?.quotes || []).find(q => q.vendorName === vendor);
                                                    return (
                                                        <th key={vendor} colSpan={makes.length}
                                                            style={{ ...thStyle, background: vendorColors[vIdx % vendorColors.length], color: '#555', fontSize: '11px' }}>
                                                            {vendorQuote?.vendorMobile || ''} {(vendorQuote?.city || vendorQuote?.location) ? `• ${vendorQuote.city || vendorQuote.location}${vendorQuote?.state ? ', ' + vendorQuote.state : ''}` : ''}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                            <tr>
                                                <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 3, background: '#283593', color: 'white', fontSize: '11px' }}>—</th>
                                                <th style={{ ...thStyle, position: 'sticky', left: 180, zIndex: 3, background: '#283593', color: 'white', fontSize: '11px' }}>—</th>
                                                <th style={{ ...thStyle, position: 'sticky', left: 260, zIndex: 3, background: '#283593', color: 'white', fontSize: '11px' }}>—</th>
                                                {vendors.map((vendor, vIdx) => {
                                                    const makes = [...new Set(
                                                        (selectedComparison?.quotes || [])
                                                            .filter(q => q.vendorName === vendor)
                                                            .map(q => q.make || 'NA')
                                                    )];
                                                    return makes.map(make => (
                                                        <th key={`${vendor}-${make}`}
                                                            style={{ ...thStyle, background: vendorColors[vIdx % vendorColors.length], color: '#555', fontSize: '11px', minWidth: 100 }}>
                                                            {make}
                                                        </th>
                                                    ));
                                                })}
                                            </tr>
                                        </thead>
                                            <tbody>
                                                {materials.map((material, mIdx) => {
                                                    const materialQuotes = selectedComparison.quotes.filter(q => q.material === material);
                                                    const uniqueUnits = [...new Set(materialQuotes.map(q => q.unit || ''))].filter(Boolean);
                                                    const uniqueQtys = [...new Set(materialQuotes.map(q => String(q.qty || '')))].filter(Boolean);
                                                    const unitDisplay = uniqueUnits.length === 1 ? uniqueUnits[0] : (uniqueUnits.length > 1 ? 'Varies' : '');
                                                    const qtyDisplay = uniqueQtys.length === 1 ? uniqueQtys[0] : (uniqueQtys.length > 1 ? 'Varies' : '');
                                                    const qtyVaries = uniqueQtys.length > 1 || uniqueUnits.length > 1;
                                                    const qtyBreakdown = materialQuotes.map(q => `${q.vendorName}: ${q.qty || '—'} ${q.unit || ''}`.trim()).join('\n');
                                                    const rowBg = mIdx % 2 === 0 ? 'white' : '#f9f9f9';

                                                    return (
                                                        <tr key={material}>
                                                            <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#e8eaf6', fontWeight: 600, zIndex: 1, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                <Tooltip title={material}>
                                                                    <span>{material}</span>
                                                                </Tooltip>
                                                            </td>
                                                            <td style={{ ...tdStyle, position: 'sticky', left: 180, background: '#e8eaf6', zIndex: 1 }}>
                                                                {qtyVaries ? (
                                                                    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{qtyBreakdown}</span>} arrow>
                                                                        <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>{unitDisplay}</span>
                                                                    </Tooltip>
                                                                ) : unitDisplay}
                                                            </td>
                                                            <td style={{ ...tdStyle, position: 'sticky', left: 260, background: '#e8eaf6', zIndex: 1 }}>
                                                                {qtyVaries ? (
                                                                    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{qtyBreakdown}</span>} arrow>
                                                                        <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>{qtyDisplay}</span>
                                                                    </Tooltip>
                                                                ) : qtyDisplay}
                                                            </td>
                                                            {vendors.map((vendor, vIdx) => {
                                                                const makes = [...new Set(
                                                                    (selectedComparison?.quotes || [])
                                                                        .filter(q => q.vendorName === vendor)
                                                                        .map(q => q.make || 'NA')
                                                                )];
                                                                return makes.map(make => {
                                                                    const quote = (pivotMap[material]?.[vendor] || []).find(q => (q.make || 'NA') === make);
                                                                    const rate = quote ? parseFloat(quote.rate) : null;
                                                                    return (
                                                                        <td key={`${vendor}-${make}`} style={{
                                                                            ...tdStyle,
                                                                            background: rate ? vendorColors[vIdx % vendorColors.length] : rowBg,
                                                                            textAlign: 'right',
                                                                            fontWeight: rate ? 600 : 400,
                                                                            color: rate ? '#1a237e' : '#999'
                                                                        }}>
                                                                            {rate ? rate.toLocaleString('en-IN') : '—'}
                                                                        </td>
                                                                    );
                                                                });
                                                            })}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ overflow: 'auto', maxHeight: '75vh' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#1a237e' }}>
                                                {['Vendor', 'Mobile', 'Contact Person', 'City', 'State', 'Material', 'Make', 'Unit', 'Qty', 'Rate', 'Bill Type', 'Remarks'].map(h => (
                                                    <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#1a237e', color: 'white', fontSize: '11px' }}>{h}</TableCell>
                                                ))}
                                                {canEdit && <TableCell sx={{ bgcolor: '#1a237e', color: 'white' }}>Actions</TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredQuotes.map((q, idx) => (
    <RawDataRow
        key={q.id}
        q={q}
        idx={idx}
        canEdit={canEdit}
        isAdmin={isAdmin}
        onEdit={(quote) => { setEditQuote({ ...quote }); setEditQuoteOpen(true); }}
        onSoftDelete={handleSoftDelete}
        onHardDelete={handleHardDelete}
    />
))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Paper>
                    )}
                </>
            )}

            <Dialog open={editQuoteOpen} onClose={() => setEditQuoteOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Quote</DialogTitle>
                <DialogContent>
                    {editQuote && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <TextField fullWidth label="Vendor Name" value={editQuote.vendorName || ''} onChange={e => setEditQuote({ ...editQuote, vendorName: e.target.value })} />
                            <TextField fullWidth label="Mobile" value={editQuote.vendorMobile || ''} onChange={e => setEditQuote({ ...editQuote, vendorMobile: e.target.value })} />
                            <TextField fullWidth label="Contact Person" value={editQuote.contactPerson || ''} onChange={e => setEditQuote({ ...editQuote, contactPerson: e.target.value })} />
                            <TextField fullWidth label="City" value={editQuote.city || editQuote.location || ''} onChange={e => setEditQuote({ ...editQuote, city: e.target.value })} />
                            <TextField fullWidth label="State" value={editQuote.state || ''} onChange={e => setEditQuote({ ...editQuote, state: e.target.value })} />
                            <TextField fullWidth label="Material" value={editQuote.material || ''} onChange={e => setEditQuote({ ...editQuote, material: e.target.value })} />
                            <TextField fullWidth label="Make" value={editQuote.make || ''} onChange={e => setEditQuote({ ...editQuote, make: e.target.value })} />
                            <TextField fullWidth label="Unit" value={editQuote.unit || ''} onChange={e => setEditQuote({ ...editQuote, unit: e.target.value })} />
                            <TextField fullWidth type="number" label="Qty" value={editQuote.qty || ''} onChange={e => setEditQuote({ ...editQuote, qty: e.target.value })} />
                            <TextField fullWidth type="number" label="Rate" value={editQuote.rate || ''} onChange={e => setEditQuote({ ...editQuote, rate: e.target.value })} />
                            <TextField fullWidth select label="Bill Type" value={editQuote.billType || 'GST Bill'} onChange={e => setEditQuote({ ...editQuote, billType: e.target.value })}>
                                <MenuItem value="GST Bill">GST Bill</MenuItem>
                                <MenuItem value="Non GST Bill">Non GST Bill</MenuItem>
                            </TextField>
                            <TextField fullWidth label="Remarks" value={editQuote.remarks || ''} onChange={e => setEditQuote({ ...editQuote, remarks: e.target.value })} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditQuoteOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateQuote}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {showTrash && isAdmin && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>🗑️ Deleted Quotes (Trash)</Typography>
                    <TableContainer component={Paper} sx={{ border: '2px solid #ffcdd2' }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#ffebee' }}>
                                <TableRow>
                                    {['Vendor', 'Material', 'Make', 'Rate', 'Bill Type', 'Deleted At', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trashedQuotes.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center">No deleted quotes</TableCell></TableRow>
                                ) : (
                                    trashedQuotes.map(q => (
                                        <TableRow key={q.id} sx={{ bgcolor: '#fff8f8' }}>
                                            <TableCell sx={{ fontSize: '11px', fontWeight: 600 }}>{q.vendorName}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{q.material}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{q.make}</TableCell>
                                            <TableCell sx={{ fontSize: '11px', textAlign: 'right' }}>{q.rate}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{q.billType}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{new Date(q.deletedAt).toLocaleString('en-IN')}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Restore">
                                                    <IconButton size="small" color="success" onClick={() => handleRestoreQuote(q.id)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Forever">
                                                    <IconButton size="small" color="error" onClick={() => handleHardDelete(q.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Vendor Comparison Excel</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Excel columns: Vender Name, Vender Mobile No., Contact Person, City, State, Material, Make, Unit, Qty, Rate, GST Bill /Non GST Bill, Remarks
                    </Alert>
                    <Button variant="outlined" fullWidth startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} sx={{ mb: 2 }}>
                        Download Template
                    </Button>
                    <Button variant="outlined" component="label" fullWidth startIcon={<UploadIcon />} sx={{ mb: 2 }}>
                        {uploadFile ? uploadFile.name : 'Select Excel File (.xlsx, .xls)'}
                        <input type="file" hidden accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files[0])} />
                    </Button>
                    <TextField fullWidth multiline rows={2} label="Notes (Optional)" value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || uploading}
                        startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}>
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="xl" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Add Vendor Quotes Manually</Typography>
                        <IconButton onClick={() => setManualOpen(false)}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ overflow: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
                            <thead>
                                <tr>
                                    {[
                                        { label: 'Vendor Name *', mandatory: true },
                                        { label: 'Mobile *', mandatory: true },
                                        { label: 'Contact Person *', mandatory: true },
                                        { label: 'City *', mandatory: true },
                                        { label: 'State *', mandatory: true },
                                        { label: 'Material *', mandatory: true },
                                        { label: 'Make', mandatory: false },
                                        { label: 'Unit', mandatory: false },
                                        { label: 'Qty', mandatory: false },
                                        { label: 'Rate', mandatory: false },
                                        { label: 'Bill Type', mandatory: false },
                                        { label: 'Remarks', mandatory: false },
                                        { label: '', mandatory: false },
                                    ].map(({ label, mandatory }) => (
                                        <th key={label} style={{ ...thStyle, background: '#1a237e', color: mandatory ? '#ff8a80' : 'white', padding: '8px' }}>{label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {manualRows.map((row, idx) => (
                                    <tr key={idx}>
                                        {['vendorName', 'vendorMobile', 'contactPerson', 'city', 'state', 'material', 'make', 'unit', 'qty', 'rate'].map(field => {
                                            const mandatoryFields = ['vendorName', 'vendorMobile', 'contactPerson', 'city', 'state', 'material'];
                                            const isMandatory = mandatoryFields.includes(field);
                                            return (
                                                <td key={field} style={{ padding: '4px' }}>
                                                    <input
                                                        value={row[field]}
                                                        onChange={(e) => {
                                                            const updated = [...manualRows];
                                                            updated[idx][field] = e.target.value;
                                                            setManualRows(updated);
                                                        }}
                                                        style={{
                                                            width: field === 'vendorName' || field === 'material' ? 140 : field === 'city' || field === 'state' ? 100 : 80,
                                                            padding: '4px', border: isMandatory && !row[field] ? '1px solid #f44336' : '1px solid #ddd',
                                                            borderRadius: 4, fontSize: '12px'
                                                        }}
                                                        placeholder={isMandatory ? 'Required' : ''}
                                                    />
                                                </td>
                                            );
                                        })}
                                        <td style={{ padding: '4px' }}>
                                            <select value={row.billType} onChange={(e) => { const u = [...manualRows]; u[idx].billType = e.target.value; setManualRows(u); }}
                                                style={{ padding: '4px', border: '1px solid #ddd', borderRadius: 4, fontSize: '12px' }}>
                                                <option value="GST">GST Bill</option>
                                                <option value="Non GST Bill">Non GST Bill</option>
                                            </select>
                                        </td>
                                        <td style={{ padding: '4px' }}>
                                            <input value={row.remarks} onChange={(e) => { const u = [...manualRows]; u[idx].remarks = e.target.value; setManualRows(u); }}
                                                style={{ width: 120, padding: '4px', border: '1px solid #ddd', borderRadius: 4, fontSize: '12px' }} />
                                        </td>
                                        <td style={{ padding: '4px' }}>
                                            <IconButton size="small" color="error" onClick={() => setManualRows(manualRows.filter((_, i) => i !== idx))}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                    <Button startIcon={<AddIcon />} onClick={() => setManualRows([...manualRows, { vendorName: '', vendorMobile: '', contactPerson: '', city: '', state: '', material: '', make: '', unit: '', qty: '', rate: '', billType: 'GST', remarks: '' }])}
                        sx={{ mt: 2 }}>
                        Add Row
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManualOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddManual}>Save Quotes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const thStyle = {
    border: '1px solid #ddd',
    padding: '10px 12px',
    textAlign: 'center',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    fontSize: '13px',
};

const tdStyle = {
    border: '1px solid #e0e0e0',
    padding: '8px 12px',
    whiteSpace: 'nowrap',
    fontSize: '13px',
};

export default VendorComparisonPage;